import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { actualRate, simpleRate } from "../fpvRates";
import { METER } from "../NeighborField";
import { audioBus } from "../audioBus";
import type { FpvParams, Telemetry } from "../hooks/useFpv";
import type { useRadio } from "../hooks/useRadio";
import type { useFlightTape } from "../hooks/useFlightTape";

const DEG = Math.PI / 180;
const BOUND = 60;
const FLOOR = -50; // hard deck, well below even a scaled-up swarm (Size ×3 ≈ -14)

function isFormField(t: EventTarget | null) {
  const el = t as HTMLElement | null;
  return !!el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
}

/**
 * Acro / rate-mode FPV flight. When active it takes over the default camera:
 * stick inputs (Mode-2 gamepad, or keyboard) set body-frame angular RATES (no
 * self-levelling), throttle drives thrust along the craft's up vector, gravity
 * and drag do the rest. The camera rides the craft with a real FPV up-tilt.
 */
export function FpvRig({
  active,
  params,
  telemetry,
  radio,
  tape,
}: {
  active: boolean;
  params: FpvParams;
  telemetry: React.MutableRefObject<Telemetry>;
  radio: ReturnType<typeof useRadio>;
  tape: ReturnType<typeof useFlightTape>;
}) {
  const { camera, scene } = useThree();
  const radioRef = useRef(radio);
  radioRef.current = radio;
  const tapeRef = useRef(tape);
  tapeRef.current = tape;
  const prevRecSw = useRef<boolean | null>(null);
  const st = useRef({
    pos: new THREE.Vector3(0, -2, 9),
    vel: new THREE.Vector3(),
    quat: new THREE.Quaternion(),
    throttle: 0.5,
    armed: false,
    prevFov: 50,
    ghostYaw: 0,      // heading while disarmed (the level repositioning ghost)
    wasArmed: false,  // frame-to-frame arm edge, to seed the ghost heading
  });
  const keys = useRef<Record<string, boolean>>({});
  const activeRef = useRef(active);
  activeRef.current = active;
  const prevArm = useRef(false);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const tmp = useRef({
    up: new THREE.Vector3(),
    dq: new THREE.Quaternion(),
    e: new THREE.Euler(),
    tilt: new THREE.Quaternion(),
    acc: new THREE.Vector3(),
    inv: new THREE.Matrix4(),
    lp: new THREE.Vector3(),
    dir: new THREE.Vector3(),
    qi: new THREE.Quaternion(),
  });

  // keyboard (only acts while FPV is active and not typing in a field)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (!activeRef.current || isFormField(e.target)) return;
      keys.current[e.code] = true;
      if (e.code === "Space") st.current.armed = !st.current.armed;
      if (e.code === "KeyR") {
        const p = paramsRef.current;
        tapeRef.current.toggleRecord({ fisheye: p.fisheye, ground: p.ground, groundAlt: p.groundAlt, groundDark: p.groundDark });
      }
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Space"].includes(e.code)) e.preventDefault();
    };
    const up = (e: KeyboardEvent) => { keys.current[e.code] = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  // entering/leaving FPV: seed pose just below & in front of the swarm (so the
  // up-tilted camera frames it and throttle climbs *up into* it) + widen FOV;
  // restore the orbit camera on exit
  useEffect(() => {
    const cam = camera as THREE.PerspectiveCamera;
    if (active) {
      st.current.prevFov = cam.fov;
      st.current.pos.set(0, -2, 9);
      st.current.vel.set(0, 0, 0);
      st.current.quat.identity();
      st.current.throttle = 0.5;
      st.current.armed = false;
      st.current.ghostYaw = 0;
      st.current.wasArmed = false;
      cam.fov = 100;
      cam.position.copy(st.current.pos); // place immediately (don't wait for first frame)
      cam.quaternion.copy(st.current.quat);
      cam.updateProjectionMatrix();
    } else {
      cam.fov = st.current.prevFov;
      cam.position.set(0, 1.5, 12);
      cam.lookAt(0, 0, 0);
      cam.updateProjectionMatrix();
    }
  }, [active, camera]);

  useFrame((_, dtRaw) => {
    if (!active) return;
    const dt = Math.min(dtRaw, 1 / 30);
    const p = paramsRef.current;
    const s = st.current;
    const k = keys.current;
    const T = tmp.current;
    const cam = camera as THREE.PerspectiveCamera;

    // --- inputs: radio (WebHID) > gamepad (Mode 2) > keyboard ---
    let yaw = 0, pitch = 0, roll = 0;
    let throttleSet = false;
    let radioArm: boolean | null = null;
    const dz = (v: number) => (Math.abs(v) < 0.06 ? 0 : v);

    const radio = radioRef.current;
    const rc = radio?.connected ? radio.channels.current : null;
    if (rc && rc.length) {
      const m = radio.mapping;
      const g = (i: number, inv: boolean) => dz((rc[i] ?? 0) * (inv ? -1 : 1));
      s.throttle = THREE.MathUtils.clamp((((rc[m.throttle] ?? -1) * (m.inv.throttle ? -1 : 1)) + 1) / 2, 0, 1);
      throttleSet = true;
      yaw = g(m.yaw, m.inv.yaw);
      pitch = g(m.pitch, m.inv.pitch);
      roll = g(m.roll, m.inv.roll);
      if (m.arm >= 0 && m.arm < rc.length) radioArm = (rc[m.arm] ?? -1) * (m.inv.arm ? -1 : 1) > 0.2;
      // record switch: flip high = start taping, flip low = stop (DVR style)
      const recCh = m.rec ?? -1;
      if (recCh >= 0 && recCh < rc.length) {
        const sw = (rc[recCh] ?? -1) * (m.inv.rec ? -1 : 1) > 0.2;
        if (prevRecSw.current !== null && sw !== prevRecSw.current && sw !== tapeRef.current.rec.current.on) {
          tapeRef.current.toggleRecord({ fisheye: p.fisheye, ground: p.ground, groundAlt: p.groundAlt, groundDark: p.groundDark });
        }
        prevRecSw.current = sw;
      }
    } else {
      const gp = Array.from(navigator.getGamepads?.() ?? []).find((g) => !!g);
      if (gp) {
        s.throttle = THREE.MathUtils.clamp((-dz(gp.axes[1] ?? 0) + 1) / 2, 0, 1);
        throttleSet = true;
        yaw = dz(gp.axes[0] ?? 0);
        roll = dz(gp.axes[2] ?? 0);
        pitch = -dz(gp.axes[3] ?? 0);
        const armBtn = !!gp.buttons[0]?.pressed;
        if (armBtn && !prevArm.current) s.armed = !s.armed;
        prevArm.current = armBtn;
      }
    }
    // keyboard augments / overrides on keypress
    if (k["KeyA"]) yaw = -1;
    if (k["KeyD"]) yaw = 1;
    if (k["ArrowUp"]) pitch = 1;
    if (k["ArrowDown"]) pitch = -1;
    if (k["ArrowLeft"]) roll = -1;
    if (k["ArrowRight"]) roll = 1;
    if (!throttleSet) {
      if (k["KeyW"]) s.throttle = Math.min(1, s.throttle + dt);
      if (k["KeyS"]) s.throttle = Math.max(0, s.throttle - dt);
    }
    // a mapped radio arm switch is authoritative; else Space / gamepad-A toggle (in s.armed)
    if (radioArm !== null) s.armed = radioArm;

    // just disarmed → seed the repositioning ghost's heading from the attitude
    if (s.wasArmed && !s.armed) {
      T.up.set(0, 0, -1).applyQuaternion(s.quat);
      s.ghostYaw = Math.atan2(-T.up.x, -T.up.z);
    }
    s.wasArmed = s.armed;

    // world scale: bigger w = the world FEELS bigger, i.e. the drone is smaller
    // and slower — all translational kinematics divide by w (rotation rates are
    // scale-free). The world geometry itself never changes.
    const w = Math.max(0.1, p.worldScale);

    if (s.armed) {
      // rate-mode angular integration in the body frame — stick → deg/s through
      // the per-axis curve (linear per-axis, or Betaflight Actual Rates)
      const act = p.rateMode === "actual";
      const rp = act ? actualRate(pitch, p.actual.pitch) : simpleRate(pitch, p.rates.pitch);
      const ry = act ? actualRate(yaw, p.actual.yaw) : simpleRate(yaw, p.rates.yaw);
      const rr = act ? actualRate(roll, p.actual.roll) : simpleRate(roll, p.rates.roll);
      T.e.set(rp * DEG * dt, ry * DEG * dt, -rr * DEG * dt, "XYZ");
      T.dq.setFromEuler(T.e);
      s.quat.multiply(T.dq).normalize();

      // thrust along body-up, plus gravity and drag
      T.up.set(0, 1, 0).applyQuaternion(s.quat);
      T.acc.copy(T.up).multiplyScalar(s.throttle * p.thrust / w);
      T.acc.y -= p.gravity / w;
      T.acc.addScaledVector(s.vel, -p.drag);
      s.vel.addScaledVector(T.acc, dt);
      s.pos.addScaledVector(s.vel, dt);

      if (s.pos.y < FLOOR) { s.pos.y = FLOOR; if (s.vel.y < 0) s.vel.y = 0; }
      s.pos.clampScalar(-BOUND, BOUND);

      // grass deck: touch it and you're respawned at the start, back in the air
      if (p.ground) {
        const groundY = -p.groundAlt * METER;
        if (s.pos.y < groundY + 0.05) {
          s.pos.set(0, Math.max(-2, groundY + 0.8), 9);
          s.vel.set(0, 0, 0);
          s.quat.identity();
          s.ghostYaw = 0;
          s.throttle = 0.5;
          telemetry.current.crash = performance.now();
        }
      }
    } else {
      // DISARMED = the repositioning ghost: level free-flight on the sticks
      // (pitch = forward, roll = strafe, throttle = climb/descend around hover,
      // yaw = turn). Arming takes over exactly where the ghost sits.
      const gs = 8 / w; // placement speed rides the world scale too
      s.ghostYaw += yaw * 2.2 * dt; // same sign convention as the armed yaw
      s.quat.setFromEuler(T.e.set(0, s.ghostYaw, 0, "XYZ"));
      T.up.set(-Math.sin(s.ghostYaw), 0, -Math.cos(s.ghostYaw)); // level forward
      T.acc.set(0, 0, 0);
      T.acc.addScaledVector(T.up, pitch * gs);
      T.dir.set(-T.up.z, 0, T.up.x); // level right
      T.acc.addScaledVector(T.dir, roll * gs);
      T.acc.y += (s.throttle - 0.5) * 2 * gs;
      s.pos.addScaledVector(T.acc, dt);
      s.vel.set(0, 0, 0);
      s.pos.clampScalar(-BOUND, BOUND);
      if (p.ground) {
        const gy = -p.groundAlt * METER + 0.3;
        if (s.pos.y < gy) s.pos.y = gy; // the ghost glides on the deck, never crashes
      } else if (s.pos.y < FLOOR) {
        s.pos.y = FLOOR;
      }
    }

    // camera rides the craft with a real FPV up-tilt (looks above the nose, so a
    // slight forward pitch cruises toward what you're framing)
    T.tilt.setFromEuler(T.e.set(p.tilt * DEG, 0, 0, "XYZ"));
    cam.position.copy(s.pos);
    cam.quaternion.copy(s.quat).multiply(T.tilt);

    // the fisheye slider widens the lens too (100° rectilinear → 140° at full);
    // the barrel distortion in FisheyeFx does the rest
    const wantFov = 100 + p.fisheye * 40;
    if (cam.fov !== wantFov) { cam.fov = wantFov; cam.updateProjectionMatrix(); }

    // proximity streaks: scan the live swarm for drones inside the warn radius
    // and light the screen edge on their side, intensity by closeness. Positions
    // are read from the field's CPU attribute (during shader morphs that's the
    // from-cloud — fine for a warning). uScale covers shader-side Size/swell.
    const prox = telemetry.current.prox;
    prox[0] = prox[1] = prox[2] = prox[3] = 0;
    if (p.streaks) {
      const pts = scene.getObjectByName("swarm") as THREE.Points | undefined;
      const attr = pts?.geometry.getAttribute("position") as THREE.BufferAttribute | undefined;
      if (pts && attr) {
        const sc = ((pts.material as THREE.ShaderMaterial).uniforms?.uScale?.value as number | undefined) ?? 1;
        T.inv.copy(pts.matrixWorld).invert();
        T.lp.copy(s.pos).applyMatrix4(T.inv); // camera in points-local space
        T.qi.copy(cam.quaternion).invert();
        const thresh = Math.max(p.warnDist * METER, 1e-3);
        const t2 = thresh * thresh;
        const arr = attr.array as Float32Array;
        const n = attr.count;
        for (let i = 0; i < n; i++) {
          const dx = arr[i * 3] * sc - T.lp.x;
          const dy = arr[i * 3 + 1] * sc - T.lp.y;
          const dz2 = arr[i * 3 + 2] * sc - T.lp.z;
          const d2 = dx * dx + dy * dy + dz2 * dz2;
          if (d2 >= t2 || d2 < 1e-6) continue;
          const w = 1 - Math.sqrt(d2) / thresh;
          // which edge: direction to the drone in camera space
          T.dir.set(dx, dy, dz2).transformDirection(pts.matrixWorld).applyQuaternion(T.qi);
          if (T.dir.x > 0) prox[1] += w * T.dir.x; else prox[0] -= w * T.dir.x;
          if (T.dir.y > 0) prox[2] += w * T.dir.y; else prox[3] -= w * T.dir.y;
        }
        prox[0] = Math.min(1, prox[0]); prox[1] = Math.min(1, prox[1]);
        prox[2] = Math.min(1, prox[2]); prox[3] = Math.min(1, prox[3]);
      }
    }

    // tape the final camera pose (tilt baked in) + sticks + audio features
    if (tapeRef.current.rec.current.on) {
      const av = audioBus.values;
      tapeRef.current.push(
        cam.position.x, cam.position.y, cam.position.z,
        cam.quaternion.x, cam.quaternion.y, cam.quaternion.z, cam.quaternion.w,
        s.throttle, yaw, pitch, roll,
        av.level, av.bass, av.mid, av.treble, av.beat,
        dt
      );
    }

    telemetry.current.throttle = s.throttle;
    telemetry.current.speed = s.vel.length();
    telemetry.current.alt = s.pos.y;
    telemetry.current.armed = s.armed;
  });

  return null;
}
